import { useEffect, useState } from 'react';
import { EODRolloverModal } from './EODRolloverModal';
import { VirginEodSchedulingModal } from './VirginEodSchedulingModal';
import { OptiPlaceModal } from './OptiPlaceModal';
import { usePermissions } from '@/hooks/usePermissions';
import {
  STARTED_WORK_EOD_EXCEEDED_EVENT,
  VIRGIN_EOD_EXCEEDED_EVENT,
  type StartedWorkEodExceededDetail,
  type VirginEodSchedulingConflictDetail,
} from '@/utils/virginEodSchedulingConflict';
// Static: the successor check must not depend on a chunk a later deploy can remove (2026-10-26).
import { liveRolloverSuccessorId } from '@/utils/rolloverChain';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
// DTC-K: route the EOD-rollover OptiPlace appointmentDate fallback
// through the canonical clock so the placement business decision is
// deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import type { Database } from '@/integrations/supabase/types';
import { fetchRolloverSourceJobs } from '@/utils/rolloverSourceJobs';
// W4: the server-raised end-of-day decision (non-blocking; approvers only — the panel asks the server).
import { RolloverDecisionPanel, openRolloverDecisionTicketIds } from '@/components/scheduling/RolloverDecisionPanel';
// W4e: reserved-window schedule conflicts / Needs Placement (approvers + dispatchers — the server decides who sees it).
import { ScheduleConflictPanel } from '@/components/scheduling/ScheduleConflictPanel';
// WS-ELIG B-E5: work held by someone who is no longer an operational worker (enforce shops; read-only signal).
import { ReassignNeededPanel } from '@/components/scheduling/ReassignNeededPanel';

// Shape of the rollover source ticket read below. A `jobs (*)` embed makes
// postgrest's inferred result deep enough to trip TS2589 (the `jobs` table's
// own row type is expensive to instantiate under this file's heavy imports),
// so the jobs are fetched separately (see `fetchRolloverSourceJobs`) and this
// ticket type is narrowed to exactly the fields consumed below.
type RolloverSourceTicket = Pick<
  Database['public']['Tables']['tickets']['Row'],
  | 'id'
  | 'vehicle_id'
  | 'shop_id'
  | 'type'
  | 'priority'
  | 'customer_name'
  | 'customer_phone'
  | 'is_comeback'
  | 'start_time'
  | 'predicted_minutes'
>;

// FLOYD 2026-10-26 (DEFECT 2): the event detail is the shared typed contract (StartedWorkEodExceededDetail) — the
// end-of-day INSTANT travels as `authorizedBoundaryAt`, `techEOD` is display text. When 'Waiter' (ticketType), the
// modal hides "Convert to Rollover"; the server-side createEODRolloverSplit also refuses Waiters.
interface EODRolloverData extends StartedWorkEodExceededDetail {
  /**
   * Set by the container: the ticket already has a rollover continuation.
   * Undefined while that is still being looked up — Convert stays hidden until
   * it is known that there is none.
   */
  alreadyCarried?: boolean;
}

export function EODRolloverContainer() {
  const [rolloverData, setRolloverData] = useState<EODRolloverData | null>(null);
  const [virginData, setVirginData] = useState<VirginEodSchedulingConflictDetail | null>(null);
  const [isOptiPlaceOpen, setIsOptiPlaceOpen] = useState(false);
  const [optiPlaceTicketData, setOptiPlaceTicketData] = useState<any>(null);

  // ROLE BOUNDARY. Both EOD surfaces ask for a DISPATCH decision — where does
  // this work go, and who does it. Bucket 3 §3.13.3 gives that decision to the
  // user who can act on it, and Bucket 5 §5.11 gives the technician RISK
  // VISIBILITY, not resolution controls: §5.6.8 is explicit that warnings
  // "must not directly modify Start Time, Tech OptiTime, OptiFinish, Cascade
  // behavior, Bay occupancy, Timer history."
  //
  // `canAssignTickets` (OWNER / MANAGER / SERVICE_WRITER / DISPATCHER /
  // ORG_ADMIN) is the placement-authority boundary this product already uses
  // for exactly this class of decision — deliberately reused rather than
  // introducing a second role list that could drift out of step with it.
  //
  // This container was previously mounted for every role with no gate at all,
  // which is how a technician came to be asked to resolve a rollover.
  const { canAssignTickets } = usePermissions();

  useEffect(() => {
    // A viewer without placement authority is never handed either decision.
    // The listeners are not registered at all, so the modals cannot open even
    // if an event is dispatched — the gate is not a render-time `&&` that a
    // later edit could drop.
    if (!canAssignTickets) return;

    const handleEODExceeded = (event: CustomEvent<EODRolloverData>) => {
      const detail = event.detail;
      // The modal opens at once; whatever raised the warning (timer resume,
      // monitor, AutoBump), a ticket that already continues is never offered
      // a second carry — Convert appears only once it is known there is no
      // continuation (the server refuses a fork regardless).
      setRolloverData({ ...detail, alreadyCarried: undefined });
      void liveRolloverSuccessorId(detail.ticketId)
        .catch(() => null)
        .then((successor) => setRolloverData((cur) =>
          cur && cur.ticketId === detail.ticketId ? { ...cur, alreadyCarried: !!successor } : cur));
    };
    const handleVirginEODExceeded = (
      event: CustomEvent<VirginEodSchedulingConflictDetail>,
    ) => {
      // W4b (D-5): when the server already raised the end-of-day decision for this ticket (the open decisions the
      // panel last read), the non-blocking decision panel IS the prompt — the legacy modal would interrupt the
      // person a second time and cover the board. A conflict the server has not raised yet still opens it.
      if (openRolloverDecisionTicketIds.has(event.detail.ticketId)) return;
      setVirginData(event.detail);
    };

    window.addEventListener(
      STARTED_WORK_EOD_EXCEEDED_EVENT, handleEODExceeded as EventListener);
    window.addEventListener(
      VIRGIN_EOD_EXCEEDED_EVENT, handleVirginEODExceeded as EventListener);
    return () => {
      window.removeEventListener(
        STARTED_WORK_EOD_EXCEEDED_EVENT, handleEODExceeded as EventListener);
      window.removeEventListener(
        VIRGIN_EOD_EXCEEDED_EVENT, handleVirginEODExceeded as EventListener);
    };
  }, [canAssignTickets]);

  /**
   * Load a ticket into OptiPlace.
   *
   * Takes the id as an argument rather than reading `rolloverData` so the
   * virgin scheduling path can reuse the SAME placement workflow. Nothing
   * about the placement itself differs — only what the operator was told the
   * decision was about.
   */
  const openPlacementFor = async (ticketId: string) => {
    if (!ticketId) return;

    try {
      // Fetch full ticket data.
      // R125-H-6: drop `vehicles (*)` join — the result is never read
      // (code uses `ticket.vehicle_id`, a scalar column on tickets). The
      // join was pulling encrypted vin/license_plate ciphertext (and
      // every other vehicles column) into the client cache for no use.
      // Narrow outer ticket select to the fields consumed below.
      //
      // The ticket fields and the ticket's jobs are fetched separately rather
      // than via a `jobs (*)` embed: a typed embed makes postgrest's inferred
      // result deep enough to trip TS2589 here (the `jobs` row type is
      // expensive to instantiate under this file's heavy imports). The narrow,
      // unembedded ticket select stays shallow; the jobs live in their own
      // module (`fetchRolloverSourceJobs`) where the budget isn't exhausted.
      const { data: ticket, error: ticketError } = await supabase
        .from('tickets')
        .select(
          'id, vehicle_id, shop_id, type, priority, customer_name, customer_phone, is_comeback, start_time, predicted_minutes',
        )
        .eq('id', ticketId)
        .single()
        .returns<RolloverSourceTicket>();

      const sourceJobs = await fetchRolloverSourceJobs(ticketId);

      if (ticketError || !ticket) {
        toast({
          title: 'Error',
          description: 'Failed to load ticket data',
          variant: 'destructive',
        });
        return;
      }

      // Get shop_id
      const { data: shopData } = await supabase
        .from('shop_profile')
        .select('id')
        .limit(1)
        .maybeSingle();

      // Prepare ticket data for OptiPlace
      const ticketData = {
        id: ticket.id,
        vehicleId: ticket.vehicle_id, // Pass existing vehicle ID for reassignment
        shop_id: shopData?.id || ticket.shop_id,
        type: ticket.type,
        priority: ticket.priority,
        customerName: ticket.customer_name,
        customerPhone: ticket.customer_phone,
        isComeback: ticket.is_comeback || false,
        appointmentDate: ticket.start_time || clock.now().toISOString(),
        jobs: sourceJobs?.map((job) => ({
          category: job.category,
          submenu: job.submenu || job.category,
          name: job.name,
          hours: job.standard_minutes ? job.standard_minutes / 60 : 0,
          minutes: job.standard_minutes || 0,
          bay: job.bay_requirement || 'Flat',
          skill: job.skill_level || 1,
          proficiency_category: job.proficiency_category,
        })) || [],
        predictedMinutes: ticket.predicted_minutes,
        startTime: ticket.type === 'Waiter' ? ticket.start_time : undefined,
      };

      setOptiPlaceTicketData(ticketData);
      setIsOptiPlaceOpen(true);
      // Close whichever EOD surface sent us here.
      setRolloverData(null);
      setVirginData(null);
    } catch (error) {
      console.error('Error preparing reassignment:', error);
      toast({
        title: 'Error',
        description: 'Failed to prepare ticket for reassignment',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <RolloverDecisionPanel />
      <ScheduleConflictPanel />
      <ReassignNeededPanel />
      <EODRolloverModal
        open={!!rolloverData}
        onClose={() => setRolloverData(null)}
        ticketId={rolloverData?.ticketId || ''}
        customerName={rolloverData?.customerName || ''}
        techName={rolloverData?.techName || ''}
        techEOD={rolloverData?.techEOD || ''}
        authorizedBoundaryAt={rolloverData?.authorizedBoundaryAt ?? null}
        rolloverMinutes={rolloverData?.rolloverMinutes || 0}
        ticketType={rolloverData?.ticketType ?? null}
        alreadyCarried={rolloverData?.alreadyCarried !== false}
        onReassign={() => { void openPlacementFor(rolloverData?.ticketId || ''); }}
        onResumeAnyway={rolloverData?.onResumeAnyway}
      />

      {/* The virgin scheduling conflict. A SEPARATE component, not a mode of
          the rollover modal — "Convert to Rollover" does not exist in this
          tree, so a never-started ticket cannot be handed a continuation it
          has no work to continue. */}
      <VirginEodSchedulingModal
        open={!!virginData}
        onClose={() => setVirginData(null)}
        customerName={virginData?.customerName || ''}
        techName={virginData?.techName || ''}
        techEOD={virginData?.techEOD || ''}
        minutesPastShiftEnd={virginData?.minutesPastShiftEnd || 0}
        onReschedule={() => { void openPlacementFor(virginData?.ticketId || ''); }}
      />


      {optiPlaceTicketData && (
        <OptiPlaceModal
          isOpen={isOptiPlaceOpen}
          onClose={() => {
            setIsOptiPlaceOpen(false);
            setOptiPlaceTicketData(null);
          }}
          ticketData={optiPlaceTicketData}
          onAssignmentComplete={(ticketId) => {
            setIsOptiPlaceOpen(false);
            setOptiPlaceTicketData(null);
            toast({
              title: 'Ticket Reassigned',
              description: 'The ticket has been successfully reassigned.',
            });
            window.location.href = `/?tab=dayboard&highlight=${ticketId}`;
          }}
        />
      )}
    </>
  );
}
